/**
 * @file Sidebar navigation shell.
 *
 * Visibility and access rules live in `useSidebarSections`; Ant Menu state and
 * navigation live in `useSiderMenuState`.
 */
import { useContext, useMemo } from "react";
import { Layout as AntLayout, Menu } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import AppContext from "../../../context/AppContext";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { isEntryProductReachable } from "../../../routing/guardTriage";
import { findFirstMatchingRoute } from "../../../routing/redirectPriority";
import useSidebarSections from "../../../hooks/useSidebarSections";
import useSiderMenuState from "../../../hooks/useSiderMenuState";
import SiderHeader from "../../shared/SiderHeader/SiderHeader";
import "./stylesSider.css";
import "./stylesSider.dev.css";

const { Sider: AntSider } = AntLayout;

const Sider = ({
  isExpanded,
  isMobileDrawerOpen = false,
  onMobileClose,
}) => {
  const effectiveExpanded = isMobileDrawerOpen || isExpanded;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    selectedDropdownOption,
    setSelectedDropdownOption,
    selectedSiderTab,
    setSelectedSiderTab,
    setWhichDropdownOpen,
    whichDropdownOpen,
    handleWhichDropdownOpen,
    setIsDropdownOpen,
    setIsExpanded,
    adminType,
    adminPermissions,
  } = useContext(AppContext);
  const sections = useSidebarSections({ adminType, adminPermissions });
  const homeRoute = useMemo(
    () =>
      findFirstMatchingRoute(adminPermissions, {
        isEntryReachable: isEntryProductReachable,
      }) ??
      findFirstMatchingRoute(adminPermissions) ??
      NAVIGATION_ROUTES?.LOGIN,
    [adminPermissions],
  );
  const {
    collapse,
    expand,
    handleMenuClick,
    handleMenuOpenChange,
    items,
    openKeys,
    selectedKeys,
  } = useSiderMenuState({
    sections,
    isExpanded: effectiveExpanded,
    pathname,
    selectedDropdownOption,
    selectedSiderTab,
    setIsDropdownOpen,
    setIsExpanded,
    setSelectedDropdownOption,
    setSelectedSiderTab,
    setWhichDropdownOpen,
    whichDropdownOpen,
    handleWhichDropdownOpen,
    navigate,
    autoOpenSelectedPath: !isMobileDrawerOpen,
  });

  if (sections.length <= 1) {
    return null;
  }

  return (
    <AntSider
      className={[
        "sider",
        effectiveExpanded ? "expanded" : "collapsed",
        isMobileDrawerOpen ? "mobile-drawer-open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      collapsed={!effectiveExpanded}
      collapsedWidth={90}
      width={280}
      trigger={null}
    >
      <SiderHeader
        isExpanded={effectiveExpanded}
        onHomeClick={() => navigate(homeRoute)}
        onToggle={
          isMobileDrawerOpen
            ? onMobileClose
            : effectiveExpanded
              ? collapse
              : expand
        }
      />

      <div className="sider-buttons">
        <Menu
          className="sider-menu"
          mode="inline"
          theme="dark"
          inlineCollapsed={!effectiveExpanded}
          items={items}
          openKeys={openKeys}
          selectedKeys={selectedKeys}
          onOpenChange={handleMenuOpenChange}
          onClick={handleMenuClick}
        />
      </div>
    </AntSider>
  );
};

export default Sider;
